<template>
  <div>
    <NavBar sticky>
      <template #left>
        <BackButton />
      </template>
    </NavBar>
    <main v-if="isLoading" class="portability__main">
      <PortabilityDetailsLoader />
    </main>
    <main v-else-if="hasError" class="portability__helper-wrapper">
      <HelperViewStatus
        class="portability__helper"
        title="Houve um problema ao carregar a solicitação"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="fetchPortabilityDetails"
      />
    </main>
    <main v-else class="portability__main">
      <HeadingDisplay
        class="portability__heading"
        :head="portability.title"
        :title="portability.custodian.name"
        :subtitle="portability.expiration.formatted"
        size="medium"
      />
      <section>
        <AssetRow
          v-for="asset in portability.assets"
          :key="asset.instrument.symbol"
          :symbol="asset.instrument.symbol"
          :description="asset.description"
          :type="asset.instrument.type"
        />
      </section>
    </main>
    <ButtonContainer class="portability__footer">
      <template #button-primary>
        <SkeletonLoader v-if="isLoading">
          <div class="portability__loader__button" />
        </SkeletonLoader>
        <ButtonPrimary
          v-else-if="showEnableMFAButton"
          :loading="isAcceptButtonLoading"
          :disabled="isAcceptButtonDisabled"
          label="Ativar verificação"
          @click="goToMFAPage"
        />
        <ButtonPrimary
          v-else
          :loading="isAcceptButtonLoading"
          :disabled="isAcceptButtonDisabled"
          label="Aprovar"
          @click="handleAcceptPortabilityRequest"
        />
      </template>
      <template #button-secondary>
        <SkeletonLoader v-if="isLoading">
          <div class="portability__loader__button" />
        </SkeletonLoader>
        <ButtonSecondary
          v-else-if="!showEnableMFAButton"
          :loading="isRejectButtonLoading"
          :disabled="isRejectButtonDisabled"
          label="Recusar"
          @click="handleRejectPortabilityRequest"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, inject, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  HeadingDisplay,
  HelperViewStatus,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';

import { AssetRow } from './components/AssetRow';
import { PortabilityDetailsLoader } from './components/PortabilityDetailsLoader';

import {
  getPortabilityDetails,
  PortabilityDetails,
  PortabilityRequestConclusionResponse,
  PortabilityRequestStatus,
  updatePortabilityRequest
} from '@/modules/trade/services/portability';
import { PortabilityRequestSteps } from '../../types';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { notify } from '@/modules/common/helpers/notification';
import { isImpersonate } from '@/modules/common/services/partner';
import { MFAModules } from '@/modules/authentication/views/SecurityView/components/MultiFactorAuthentication';

const isLoading = ref(true);
const hasError = ref(false);
const isAcceptingPortability = ref(false);
const isRejectingPortability = ref(false);

const portability = inject(
  'portabilityRequest'
) as Ref<PortabilityDetails | null>;
const portabilityRequestConclusion = inject(
  'portabilityRequestConclusion'
) as Ref<PortabilityRequestConclusionResponse | null>;

interface Emit {
  (e: 'on-change-step', step: PortabilityRequestSteps): void;
}
const emit = defineEmits<Emit>();

const route = useRoute();
const router = useRouter();

const portabilityId = computed(() => {
  return route.params.id;
});

const isImpersonateUser = computed(() => {
  return isImpersonate();
});

const isAcceptButtonLoading = computed(() => {
  return isLoading.value || isAcceptingPortability.value;
});

const isAcceptButtonDisabled = computed(() => {
  return (
    hasError.value || isRejectingPortability.value || isImpersonateUser.value
  );
});

const isRejectButtonLoading = computed(() => {
  return isLoading.value || isRejectingPortability.value;
});

const isRejectButtonDisabled = computed(() => {
  return (
    hasError.value || isAcceptingPortability.value || isImpersonateUser.value
  );
});

const showEnableMFAButton = computed(() => {
  return !portability.value?.enabledMFA;
});

const handleAcceptPortabilityRequest = async () => {
  isAcceptingPortability.value = true;
  await updatePortability(PortabilityRequestStatus.accept);
  isAcceptingPortability.value = false;
};

const handleRejectPortabilityRequest = async () => {
  isRejectingPortability.value = true;
  await updatePortability(PortabilityRequestStatus.reject);
  isRejectingPortability.value = false;
};

const updatePortability = async (status: PortabilityRequestStatus) => {
  try {
    const data = await updatePortabilityRequest(status, portabilityId.value);

    portabilityRequestConclusion.value = data;

    emit('on-change-step', PortabilityRequestSteps.Conclusion);
  } catch {
    const action =
      status === PortabilityRequestStatus.accept ? 'aprovar' : 'recusar';

    notify({
      group: 'wrn-notifications',
      text: `Ocorreu um problema ao ${action} a solicitação. Tente novamente.`,
      type: NotificationTypes.error
    });
  }
};

const fetchPortabilityDetails = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;
    const response = await getPortabilityDetails(portabilityId.value);

    portability.value = response;
  } catch (e) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const goToMFAPage = () => {
  router.push({
    name: 'security',
    query: {
      module: MFAModules.TRADE
    }
  });
};

onMounted(async () => {
  await fetchPortabilityDetails();
});
</script>
<style lang="less" scoped>
.portability {
  display: flex;
  min-height: 100vh;
  flex-direction: column;

  &__loader {
    &__button {
      height: 48px;
      width: 120px;
      border-radius: @size-radius-x200;
    }
  }

  &__main {
    padding: @size-spacing-x1000 @size-spacing-x500 @size-spacing-x1200
      @size-spacing-x500;
    max-width: 592px;
    width: 100%;
    margin: 0 auto;
    box-sizing: content-box;
    .to-lg({ padding: @size-spacing-x400 @size-spacing-x400 @size-spacing-x1200 @size-spacing-x400; });
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: @background-secondary;

    .portability__loader__button:first-of-type {
      margin-right: @size-spacing-x300;
    }
  }

  &__heading {
    margin-bottom: @size-spacing-x800;
  }

  &__helper-wrapper {
    flex-grow: 1;
    display: flex;
  }

  &__helper {
    display: block;
    margin: 0 auto;
    align-self: center;
  }
}
</style>
